<template>
  <v-flex class="view-payment-page">
    <!-- Toolbar superior -->
    <div class="page-toolbar">
      <v-btn v-if="hasHistory" icon @click="$router.go(-1)" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon class="mr-2" color="primary">mdi-receipt-text-arrow-right</v-icon>
      <span class="toolbar-title">Detalle del Pago Directo</span>
      <v-spacer></v-spacer>

      <v-btn
        v-if="!isView"
        @click="downloadInvoice"
        outlined
        small
        color="primary"
        class="rounded text-capitalize toolbar-btn"
      >
        <v-icon left small>mdi-printer</v-icon>
        Imprimir
      </v-btn>
      <v-btn
        v-else
        @click="isView = false"
        outlined
        small
        color="primary"
        class="rounded text-capitalize toolbar-btn"
      >
        <v-icon left small>mdi-arrow-left</v-icon>
        Volver al detalle
      </v-btn>
    </div>

    <!-- Loading -->
    <v-row v-if="loading" justify="center" class="mt-12">
      <v-col cols="12" md="8" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64" class="mb-4" />
        <h3 class="grey--text">Cargando información del pago...</h3>
      </v-col>
    </v-row>

    <!-- Visor PDF -->
    <div v-else-if="isView">
      <iframe
        v-if="pdfUrl"
        class="pdf-viewer"
        :src="pdfUrl"
        frameborder="0"
        allowfullscreen
        :title="`PDF del pago directo #${id_transactions}`"
      ></iframe>
    </div>

    <!-- Contenido principal -->
    <div v-else>
      <v-container fluid class="payment-detail-container">
        <v-row v-if="data_payment">
          <!-- Recibo -->
          <v-col cols="12" lg="7">
            <v-card class="payment-receipt" elevation="3">
              <!-- Header del recibo -->
              <div class="receipt-header" :class="receiptHeaderClass">
                <v-chip
                  small
                  :color="isPago ? 'red lighten-5' : 'green lighten-5'"
                  :text-color="isPago ? 'red darken-3' : 'green darken-3'"
                  class="font-weight-bold receipt-chip"
                >
                  <v-icon left x-small>
                    {{ isPago ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
                  </v-icon>
                  {{ isPago ? 'PAGO DIRECTO' : 'COBRO DIRECTO' }}
                </v-chip>
                <div class="receipt-number">
                  N° {{ filLeft($route.params.id.toString(), 8, "0") }}
                </div>
                <div class="receipt-location mt-2">
                  <v-icon small color="white" class="mr-1">mdi-map-marker</v-icon>
                  {{ data_payment.location_description }}
                </div>
              </div>

              <!-- Contenido -->
              <v-card-text class="receipt-content">
                <!-- Entidad -->
                <div class="info-block">
                  <div class="info-label">
                    <v-icon small left color="grey">mdi-account</v-icon>
                    {{ isPago ? 'Proveedor' : 'Cliente' }}
                  </div>
                  <div class="info-value">
                    <span v-if="data_payment.entity_name">{{ data_payment.entity_name }}</span>
                    <span v-else class="grey--text font-italic">— Sin entidad —</span>
                  </div>
                </div>

                <v-row>
                  <v-col cols="12" sm="6">
                    <div class="info-block">
                      <div class="info-label">
                        <v-icon small left color="grey">mdi-calendar</v-icon>
                        Fecha del pago
                      </div>
                      <div class="info-value">{{ getDate(data_payment.payment_date) }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="info-block">
                      <div class="info-label">
                        <v-icon small left color="grey">mdi-calendar-check</v-icon>
                        Fecha contable
                      </div>
                      <div class="info-value">{{ getDate(data_payment.account_date) }}</div>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="6">
                    <div class="info-block">
                      <div class="info-label">
                        <v-icon small left color="grey">mdi-bank</v-icon>
                        Cuenta bancaria
                      </div>
                      <div class="info-value">
                        Cta. {{ data_payment.bank_account_id }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="info-block">
                      <div class="info-label">
                        <v-icon small left color="grey">mdi-credit-card</v-icon>
                        Método de pago
                      </div>
                      <div class="info-value">{{ data_payment.payment_type_name }}</div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Total destacado -->
                <div class="total-section" :class="isPago ? 'total-pago' : 'total-cobro'">
                  <div class="total-label">
                    {{ isPago ? 'Total pagado' : 'Total cobrado' }}
                  </div>
                  <div class="total-amount">
                    {{ currencyFormat(data_payment.total_amount) }}
                    <span class="currency">{{ data_payment.currency_code }}</span>
                  </div>
                  <div v-if="data_payment.currency_rate && data_payment.currency_rate != 1" class="total-rate">
                    Tasa: {{ data_payment.currency_rate }}
                  </div>
                </div>

                <!-- Nota -->
                <div v-if="data_payment.note" class="note-block">
                  <div class="info-label">
                    <v-icon small left color="grey">mdi-note-text</v-icon>
                    Nota
                  </div>
                  <p class="note-content">{{ data_payment.note }}</p>
                </div>

                <v-divider class="my-4"></v-divider>

                <!-- Procesado por -->
                <div class="processed-by-block">
                  <v-avatar size="32" color="primary" class="white--text mr-2">
                    <span class="caption font-weight-bold">
                      {{ userInitials(data_payment.payment_by_fullname) }}
                    </span>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text">Procesado por</div>
                    <div class="font-weight-medium">
                      {{ data_payment.payment_by_fullname }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Archivos adjuntos -->
          <v-col cols="12" lg="5">
            <v-card class="attachments-card h-100" elevation="2">
              <v-card-title class="attachments-header">
                <v-icon left color="primary">mdi-paperclip</v-icon>
                <span class="font-weight-medium">Archivos Adjuntos</span>
                <v-spacer></v-spacer>
                <v-chip v-if="selectedAttached" x-small color="primary" outlined>
                  Visualizando
                </v-chip>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="attachments-content">
                <!-- Estado vacío -->
                <div v-if="selectedAttached == ''" class="empty-attachments">
                  <v-btn
                    v-if="hasFeature('CXC-042') || hasFeature('CXP-022')"
                    @click="takeImage"
                    class="upload-button"
                    color="primary"
                    outlined
                    large
                    block
                  >
                    <v-icon left size="32">mdi-cloud-upload</v-icon>
                    <div>
                      <div class="font-weight-bold">Subir archivo</div>
                      <div class="caption">Haz clic para adjuntar documentos</div>
                    </div>
                  </v-btn>

                  <div class="upload-help mt-4">
                    <v-icon color="grey" small class="mr-1">mdi-information-outline</v-icon>
                    <span class="caption grey--text">
                      Formatos: PDF, JPG, PNG, DOC, DOCX
                    </span>
                  </div>
                </div>

                <!-- Visor -->
                <div v-else class="file-viewer">
                  <div class="file-controls">
                    <v-btn @click="selectedAttached = ''" text small>
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Volver
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn v-if="hasFeature('CXC-042') || hasFeature('CXP-022')" @click="takeImage" color="primary" outlined small>
                      <v-icon left small>mdi-plus</v-icon>
                      Agregar más
                    </v-btn>
                  </div>

                  <div class="iframe-container">
                    <iframe :src="selectedAttached" frameborder="0" class="attachment-iframe" />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Input file oculto -->
        <v-file-input
          v-show="false"
          name="upload"
          id="fileInput"
          @change="onFileChange"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          multiple
        />

        <!-- Snackbars -->
        <v-snackbar v-model="showSuccess" color="success" timeout="3000" top>
          <v-icon left>mdi-check-circle</v-icon>
          {{ successMessage }}
        </v-snackbar>
        <v-snackbar v-model="showError" color="error" timeout="5000" top>
          <v-icon left>mdi-alert-circle</v-icon>
          {{ errorMessage }}
        </v-snackbar>

        <!-- Tabs -->
        <v-card class="tabs-card mt-6" elevation="2">
          <v-tabs v-model="tab" background-color="white" color="primary" show-arrows>
            <v-tab class="text-capitalize">
              <v-icon left small>mdi-book-open-variant</v-icon>
              Contabilidad
              <v-chip x-small class="ml-2" color="primary" outlined v-if="accounting.length">
                {{ accounting.length }}
              </v-chip>
            </v-tab>
            <v-tab v-if="hasInvoices" class="text-capitalize">
              <v-icon left small>mdi-file-document-multiple</v-icon>
              Facturas
              <v-chip x-small class="ml-2" color="primary" outlined>
                {{ data_payment.prepayment_details.length }}
              </v-chip>
            </v-tab>
          </v-tabs>
          <v-divider></v-divider>

          <v-tabs-items v-model="tab">
            <!-- TAB: Contabilidad -->
            <v-tab-item>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="headersContabilidad"
                  :items="accounting"
                  class="accounting-table"
                  hide-default-footer
                  :items-per-page="100"
                  no-data-text="No hay registros contables disponibles"
                >
                  <template v-slot:[`item.chart_of_account_name`]="{ item }">
                    <div class="account-info">
                      <v-chip x-small color="primary" outlined class="font-weight-bold">
                        {{ item.chart_of_account_code }}
                      </v-chip>
                      <span class="account-name ml-2">{{ item.chart_of_account_name }}</span>
                    </div>
                  </template>

                  <template v-slot:[`item.debito`]="{ item }">
                    <span
                      v-if="item.entry_type_id == 1"
                      class="debit-amount red--text text--darken-2 font-weight-bold"
                    >
                      {{ currencyFormat(item.amount) }} {{ item.currency_code }}
                    </span>
                    <span v-else class="grey--text">—</span>
                  </template>

                  <template v-slot:[`item.credito`]="{ item }">
                    <span
                      v-if="item.entry_type_id == 2"
                      class="credit-amount green--text text--darken-2 font-weight-bold"
                    >
                      {{ currencyFormat(item.amount) }} {{ item.currency_code }}
                    </span>
                    <span v-else class="grey--text">—</span>
                  </template>

                  <template slot="body.append" v-if="accounting.length">
                    <tr class="totals-row">
                      <td class="font-weight-bold totals-label">
                        <v-icon left small color="primary">mdi-calculator</v-icon>
                        TOTALES
                      </td>
                      <td></td>
                      <td class="text-right">
                        <v-chip color="red darken-2" text-color="white" small class="font-weight-bold">
                          {{ currencyFormat(getTotalDebits) }} {{ accounting[0].currency_code }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <v-chip color="green darken-2" text-color="white" small class="font-weight-bold">
                          {{ currencyFormat(getTotalCredits) }} {{ accounting[0].currency_code }}
                        </v-chip>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <!-- TAB: Facturas (solo si hay) -->
            <v-tab-item v-if="hasInvoices">
              <v-card-text>
                <v-data-table
                  :headers="headers_invoices"
                  :items="data_payment.prepayment_details"
                  :items-per-page="100"
                  dense
                  class="elevation-0"
                  fixed-header
                >
                  <template v-slot:[`item.invoices.invoice_number`]="{ item }">
                    <span
                      class="primary--text font-weight-medium"
                      style="cursor: pointer"
                      @click="showInvoice(item.invoices.invoice_id, true)"
                    >
                      <v-icon left small color="primary">mdi-file-document</v-icon>
                      {{ item.invoices.invoice_number }}
                    </span>
                  </template>

                  <template v-slot:[`item.invoices.amount`]="{ item }">
                    <span class="font-weight-medium">
                      {{ currencyFormat(item.invoices.invoice_total_amount) }}
                      <span class="caption grey--text">{{ item.invoices.currency_code }}</span>
                    </span>
                  </template>

                  <template v-slot:[`item.invoices.payment_id`]="{ item }">
                    <v-hover v-slot="{ hover }">
                      <span
                        @click="showPayments(item.invoices.payment_id, 1)"
                        class="payment-link"
                        :class="{ 'payment-link-hover': hover }"
                      >
                        {{ filLeft(item.invoices.payment_id.toString(), 8, "0") }}
                      </span>
                    </v-hover>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-container>
    </div>
  </v-flex>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    tab: null,
    accounting: [],
    hasHistory: false,
    headersContabilidad: [
      { text: "Asiento", value: "diary_book_code", align: "start", sortable: false, width: "20%" },
      { text: "Cuenta", value: "chart_of_account_name", width: "40%" },
      { text: "Débito", value: "debito", align: "right", width: "20%" },
      { text: "Crédito", value: "credito", align: "right", width: "20%" },
    ],
    headers_invoices: [
      { text: "Factura", align: "start", value: "invoices.invoice_number" },
      { text: "Monto de la factura", value: "invoices.amount", align: "end" },
      { text: "Pago", value: "invoices.payment_id", align: "center" },
      { text: "Monto del pago", value: "invoices.amount", align: "end" },
    ],
    isView: false,
    pdfUrl: null,
    id_transactions: null,
    data_payment: null,
    selectedAttached: "",
    loading: false,
    showSuccess: false,
    showError: false,
    successMessage: "",
    errorMessage: "",
  }),
  computed: {
    isPago() {
      if (!this.data_payment) return false;
      return [1, 4].includes(parseInt(this.data_payment.invoice_type_id));
    },
    receiptHeaderClass() {
      return this.isPago ? "receipt-header-pago" : "receipt-header-cobro";
    },
    hasInvoices() {
      return (
        this.data_payment &&
        Array.isArray(this.data_payment.prepayment_details) &&
        this.data_payment.prepayment_details.length > 0
      );
    },
    getTotalDebits() {
      return this.accounting.reduce(
        (acc, entry) => (entry.entry_type_id === 1 ? acc + parseFloat(entry.amount) : acc),
        0
      );
    },
    getTotalCredits() {
      return this.accounting.reduce(
        (acc, entry) => (entry.entry_type_id === 2 ? acc + parseFloat(entry.amount) : acc),
        0
      );
    },
  },
  created() {
    this.getData();
  },
  mounted() {
    this.hasHistory = window.history.length > 2;
  },
  methods: {
    userInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    takeImage() {
      document.getElementById("fileInput").click();
    },
    async onFileChange() {
      try {
        const fileInput = document.getElementById("fileInput");
        if (!fileInput.files || fileInput.files.length === 0) return;

        this.loading = true;
        const record = await this.upload_images("orders_atached", fileInput);
        const responseRequest = await this.api_Post("transactions/add_storage_payment", {
          id: this.id_transactions,
          storage: record,
        });

        if (responseRequest.status == 200) {
          this.showSuccessMessage("Archivo adjuntado correctamente");
          this.getData();
        } else {
          this.showErrorMessage("Error al cargar el archivo");
        }
      } catch (e) {
        this.showErrorMessage("Error: " + e.message);
      } finally {
        this.loading = false;
      }
    },
    async getData() {
      this.loading = true;
      this.id_transactions = this.$route.params.id;

      try {
        const responseRequest = await this.api_Post(
          "transactions/view_direct_payment_by_id",
          { filters: "WHERE PREPAYMENT_ID=" + this.id_transactions }
        );

        if (responseRequest.status == 200) {
          const data = await responseRequest.json();
          this.data_payment = data[0];

          const responseAccounting = await this.api_Post("accounting/payment_accounting", {
            filters: `where direct_payment_id = ${this.id_transactions}`,
          });
          if (responseAccounting.status == 200) {
            this.accounting = await responseAccounting.json();
          }
        } else {
          this.showErrorMessage("Error al cargar la información del pago");
        }
      } catch (e) {
        this.showErrorMessage("Error de conexión: " + e.message);
      } finally {
        this.loading = false;
      }
    },
    getDate(item) {
      return item ? moment(item).format("DD/MM/YYYY") : "—";
    },
    async downloadInvoice() {
      this.loading = true;
      this.isView = true;
      try {
        const responseDataPdf = await this.api_Post("transactions/view_direct_payment_pdf", {
          id: this.id_transactions,
        });
        const blob = await responseDataPdf.blob();
        this.pdfUrl = URL.createObjectURL(blob);
      } catch (error) {
        this.showErrorMessage("Error al cargar el PDF");
        this.isView = false;
      } finally {
        this.loading = false;
      }
    },
    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccess = true;
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.showError = true;
    },
    filLeft(str, length, char) {
      return str.padStart(length, char);
    },
    currencyFormat(amount) {
      return new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount || 0));
    },
  },
};
</script>

<style scoped>
.view-payment-page {
  background-color: #f4f6f9;
  min-height: 100vh;
}

.page-toolbar {
  display: flex;
  align-items: center;
  padding: 14px 24px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
}

.toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: #424242;
}

.toolbar-btn {
  border-radius: 8px !important;
  text-transform: none !important;
}

.pdf-viewer {
  min-height: 90vh;
  width: 100%;
}

.payment-detail-container {
  padding: 24px;
}

/* Recibo */
.payment-receipt {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08) !important;
}

.receipt-header {
  padding: 20px 24px;
  color: white;
  text-align: center;
  position: relative;
}

.receipt-header-pago {
  background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
}

.receipt-header-cobro {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
}

.receipt-chip {
  position: absolute;
  top: 16px;
  left: 16px;
}

.receipt-number {
  font-family: 'Courier New', monospace;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 2px;
  margin-top: 8px;
}

.receipt-location {
  font-size: 13px;
  opacity: 0.95;
}

.receipt-content {
  padding: 24px !important;
}

.info-block {
  margin-bottom: 18px;
}

.info-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.info-value {
  color: #212121;
  font-size: 15px;
  font-weight: 500;
}

/* Total destacado */
.total-section {
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
}

.total-pago {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border: 1px solid #ef9a9a;
}

.total-cobro {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 1px solid #a5d6a7;
}

.total-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #424242;
  margin-bottom: 6px;
}

.total-amount {
  font-size: 32px;
  font-weight: 700;
  color: #212121;
  font-family: 'Roboto Mono', monospace;
}

.currency {
  font-size: 18px;
  color: #616161;
  margin-left: 4px;
}

.total-rate {
  font-size: 11px;
  color: #757575;
  margin-top: 6px;
}

.note-block {
  background-color: #fffde7;
  border-left: 3px solid #fdd835;
  padding: 10px 14px;
  border-radius: 6px;
  margin-top: 16px;
}

.note-content {
  margin: 4px 0 0;
  color: #5d4037;
  font-size: 13px;
  white-space: pre-wrap;
}

.processed-by-block {
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: #fafafa;
  border-radius: 8px;
}

/* Attachments */
.attachments-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.attachments-header {
  background-color: #fafbfc;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  padding: 14px 20px;
}

.attachments-content {
  padding: 24px !important;
  height: calc(100% - 64px);
}

.empty-attachments {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
}

.upload-button {
  border: 2px dashed #1976d2 !important;
  border-radius: 12px !important;
  padding: 32px 24px !important;
  transition: all 0.3s ease;
}

.upload-button:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
  border-color: #1565c0 !important;
}

.upload-help {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-viewer {
  height: 100%;
}

.file-controls {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.iframe-container {
  position: relative;
  width: 100%;
  height: calc(100% - 60px);
  min-height: 400px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.attachment-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Tabs */
.tabs-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.accounting-table {
  border-radius: 0;
}

.account-info {
  display: flex;
  align-items: center;
}

.account-name {
  color: #424242;
  font-size: 14px;
}

.debit-amount,
.credit-amount {
  font-size: 14px;
  font-family: 'Roboto Mono', monospace;
}

.totals-row {
  background-color: #f5f5f5 !important;
  border-top: 2px solid #e0e0e0;
}

.totals-row td {
  padding: 14px 16px !important;
  font-size: 14px;
}

.totals-label {
  color: #424242;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.payment-link {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #424242;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.payment-link-hover {
  background-color: #e3f2fd;
  color: #1565c0 !important;
}

/* Responsive */
@media (max-width: 960px) {
  .payment-detail-container {
    padding: 16px;
  }
  .receipt-header {
    padding: 24px 16px;
  }
  .receipt-content {
    padding: 18px !important;
  }
  .total-amount {
    font-size: 26px;
  }
}
</style>
